import React, { FC, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import useHorizontalScroll from 'hooks/useHorizontalScroll';
import { AppGroup, Application } from 'reduxState/store/application/types';
import './AppButtonsList.scss';
import { trimAppName } from '../helpers';

/**
 * Represents a list of buttons with application names per button.  On click, it will navigate the user to the Application page of the specified app.
 *
 * @param props - The properties for the AppButton component.
 * @param props.appGroup - The App Group that contains all the applications and admin app id
 *
 * @example
 * ```tsx
 * <AppButtonsList
 *  appGroup={{
 *  adminId: '47`
 *  adminName: 'Acorns`
 *  applications: [{
 *  id: 46,
 *  name: 'Acorns Chrome Extension Client',
 *  partnerId: 'acorns',
 *  paymentName: 'Acorns Advisers, LLC'
 * }]
 * }}
 * />
 * ```
 */

interface AppButtonsListProps {
  appGroup: AppGroup;
  isRestrictedOpsUser?: boolean; // for entity 74 ops restricted user
}

const AppButtonsList: FC<React.PropsWithChildren<AppButtonsListProps>> = ({ appGroup, isRestrictedOpsUser }) => {
  const apps = appGroup.applications || [];
  const location = useLocation();
  const { scrollContainerRef, canScrollLeft, canScrollRight, scroll, checkForScroll } = useHorizontalScroll();

  useEffect(checkForScroll, [apps, checkForScroll]);

  const isAllApplicationsActive = location.pathname.includes('/app-group');
  const pathName = isRestrictedOpsUser ? 'commission-history' : location.pathname.split('/').pop() || 'summary';

  return (
    <div className="app-button-list-scroll-icon-boundary">
      {canScrollLeft && (
        <IconButton
          className="scroll-icon left"
          onClick={() => scroll(-400)}
          sx={{
            ':hover': {
              backgroundColor: 'var(--dark-purple)',
            },
          }}
        >
          <ChevronLeftIcon sx={{ color: 'white' }} />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          className="scroll-icon right"
          onClick={() => scroll(400)}
          sx={{
            ':hover': {
              backgroundColor: 'var(--dark-purple)',
            },
          }}
        >
          <ChevronRightIcon sx={{ color: 'white' }} />
        </IconButton>
      )}

      <div
        ref={scrollContainerRef}
        className={`app-buttons-list ${canScrollLeft || canScrollRight ? 'horizontal-scroll' : ''}`}
      >
        {apps.length > 1 && (
          <Link
            key="all-applications"
            className={`app-button text-dark-purple hover:border-b-4 hover:border-b-dark-purple hover:no-underline hover:text-dark-purple focus:text-dark-purple focus:no-underline ${
              isAllApplicationsActive ? 'border-b-4 border-b-dark-purple no-underline' : ''
            }`}
            to={`/${appGroup.adminId}/${isRestrictedOpsUser ? 'app-group-commission-history' : 'app-group-summary'}`}
          >
            App Group Overview
          </Link>
        )}
        {apps.map((app: Application) => {
          const isAppActive = location.pathname.includes(`/app/${app.id}/`);

          return (
            <Link
              key={`${app.name}`}
              className={`app-button text-dark-purple hover:border-b-4 hover:border-b-dark-purple hover:no-underline hover:text-dark-purple focus:text-dark-purple focus:no-underline ${
                isAppActive ? 'border-b-4 border-b-dark-purple no-underline' : ''
              }`}
              to={`/${appGroup.adminId}/app/${app.id}/${pathName}`}
            >
              {trimAppName(app.name, appGroup.adminName)}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default AppButtonsList;
